import EmptyState from "@galaxy-io/dls/feedback/EmptyState";
import Skeleton from "@galaxy-io/dls/feedback/Skeleton";
import Timeline from "@galaxy-io/dls/timeline/Timeline";
import { type ReviewState, reviewLabels } from "@gtm/contracts/core/review";
import { ClockCounterClockwiseIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { QueryState } from "@/components/query-state";
import { DrawerSection } from "@/components/workspace/drawer-content";
import { apiClient, unwrap } from "@/lib/api-client";
import { date } from "@/lib/utils";

export function ReviewHistory({ recordKey }: { recordKey: string }) {
  const history = useQuery({
    queryKey: ["review-history", recordKey],
    queryFn: ({ signal }) =>
      unwrap(
        apiClient().GET("/reviews/history", {
          params: { query: { key: recordKey } },
          signal,
        }),
      ),
  });
  const { refetch } = history;
  useEffect(() => {
    const refresh = () => {
      void refetch();
    };
    window.addEventListener("records:changed", refresh);
    return () => window.removeEventListener("records:changed", refresh);
  }, [refetch]);
  return (
    <DrawerSection header="Status history" icon={ClockCounterClockwiseIcon}>
      {history.error ? (
        <QueryState error={history.error} onRetry={() => void refetch()} />
      ) : history.isPending ? (
        <Skeleton lines={3} />
      ) : history.data.length ? (
        <Timeline
          aria-label="Status changes"
          items={history.data.map((entry) => ({
            id: entry.id,
            label: `${entry.action === "snapshot" ? "Recorded status: " : entry.action === "retry" ? "Slack retry · " : entry.previousState ? `${reviewLabels[entry.previousState as ReviewState] || entry.previousState} → ` : ""}${reviewLabels[entry.state as ReviewState] || entry.state}`,
            description:
              entry.actorName ||
              (entry.actorUserId ? "Former member" : entry.createdByName || "Unknown user"),
            time: date(entry.createdAt, true),
            dateTime: entry.createdAt,
          }))}
        />
      ) : (
        <EmptyState
          header="No status changes yet"
          description="Review actions and their authors appear here."
        />
      )}
    </DrawerSection>
  );
}
